/* La striscia della settimana: un giorno per colonna, con le sue ore.
 *
 * Nata il 25 settembre 2026 come griglia pratiche-per-giorni sotto il modulo,
 * e rifatta la stessa notte: la griglia, il modulo e l'elenco delle ultime due
 * settimane mostravano la stessa cosa tre volte, e chi imputa non capiva da
 * dove partire. **Ora c'e' un'idea sola: un giorno scelto.** La striscia lo
 * sceglie, il foglio sotto mostra cosa c'e' in quel giorno e ci scrive. E' il
 * modello della vista giornaliera di Harvest.
 *
 * **Il giorno vuoto si vede da solo**, ed e' la leva contro il difetto 2 che
 * un elenco non da': un elenco mostra cosa c'e', non cosa manca. Si segnala
 * solo il feriale gia' finito, non oggi: la giornata non e' chiusa. Nessun
 * totale atteso: quante ore una persona dovrebbe fare il programma non lo sa.
 */

import { oggi, oreEMinuti, type Imputazione } from "../dominio";

const NOMI_GIORNI = ["lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica"];

function daIso(iso: string): Date {
  const [anno, mese, giorno] = iso.split("-").map(Number);
  return new Date(Date.UTC(anno ?? 1970, (mese ?? 1) - 1, giorno ?? 1));
}

function aIso(data: Date): string {
  return data.toISOString().slice(0, 10);
}

export function aggiungiGiorni(iso: string, quanti: number): string {
  const data = daIso(iso);
  data.setUTCDate(data.getUTCDate() + quanti);
  return aIso(data);
}

/** Il lunedi' della settimana che contiene `iso`. In UTC, cosi' il cambio
 * dell'ora legale non sposta nessun giorno. */
export function lunediDi(iso: string): string {
  const giornoSettimana = (daIso(iso).getUTCDay() + 6) % 7;
  return aggiungiGiorni(iso, -giornoSettimana);
}

/** "Venerdì 26 settembre": il titolo del foglio del giorno. */
export function giornoPerTitolo(iso: string): string {
  const d = daIso(iso);
  const nome = NOMI_GIORNI[(d.getUTCDay() + 6) % 7] ?? "";
  const mese = d.toLocaleDateString("it-IT", { month: "long", timeZone: "UTC" });
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)} ${d.getUTCDate()} ${mese}`;
}

function periodo(lunedi: string): string {
  const inizio = daIso(lunedi);
  const fine = daIso(aggiungiGiorni(lunedi, 6));
  const mese = (d: Date) => d.toLocaleDateString("it-IT", { month: "long", timeZone: "UTC" });
  if (inizio.getUTCMonth() === fine.getUTCMonth()) {
    return `dal ${inizio.getUTCDate()} al ${fine.getUTCDate()} ${mese(fine)} ${fine.getUTCFullYear()}`;
  }
  return `dal ${inizio.getUTCDate()} ${mese(inizio)} al ${fine.getUTCDate()} ${mese(fine)} ${fine.getUTCFullYear()}`;
}

interface Props {
  lunedi: string;
  scelto: string;
  imputazioni: Imputazione[];
  scegli: (giorno: string) => void;
  esporta: () => void;
}

export function Striscia({ lunedi, scelto, imputazioni, scegli, esporta }: Props) {
  const oggiIso = oggi();
  const settimanaCorrente = lunediDi(oggiIso);
  const giorni = Array.from({ length: 7 }, (_, i) => aggiungiGiorni(lunedi, i));
  // Sabato e domenica solo se ci si e' lavorato: altrimenti sono due colonne
  // vuote che spingono fuori il resto.
  const conFineSettimana = imputazioni.some((i) => i.giorno >= giorni[5]!) || scelto >= giorni[5]!;
  const colonne = conFineSettimana ? giorni : giorni.slice(0, 5);

  const perGiorno = new Map<string, number>();
  for (const i of imputazioni) perGiorno.set(i.giorno, (perGiorno.get(i.giorno) ?? 0) + i.minuti);
  const totale = imputazioni.reduce((s, i) => s + i.minuti, 0);

  return (
    <section className="striscia" aria-label="Settimana">
      <div className="striscia-testa">
        <div className="striscia-scorri">
          <button
            type="button"
            className="tasto tasto-icona"
            aria-label="Settimana prima"
            onClick={() => scegli(aggiungiGiorni(lunedi, -7))}
          >
            ‹
          </button>
          <span className="striscia-periodo">{periodo(lunedi)}</span>
          <button
            type="button"
            className="tasto tasto-icona"
            aria-label="Settimana dopo"
            disabled={lunedi >= settimanaCorrente}
            onClick={() => scegli(aggiungiGiorni(lunedi, 7))}
          >
            ›
          </button>
          {scelto !== oggiIso && (
            <button type="button" className="tasto tasto-piccolo" onClick={() => scegli(oggiIso)}>
              Oggi
            </button>
          )}
        </div>
        <div className="striscia-totale">
          <span>
            Settimana <strong>{oreEMinuti(totale)}</strong>
          </span>
          {imputazioni.length > 0 && (
            <button type="button" className="tasto tasto-nudo" onClick={esporta}>
              Esporta in Excel
            </button>
          )}
        </div>
      </div>

      <div className="striscia-giorni" style={{ gridTemplateColumns: `repeat(${colonne.length}, 1fr)` }}>
        {colonne.map((g, i) => {
          const minuti = perGiorno.get(g) ?? 0;
          const futuro = g > oggiIso;
          const vuoto = minuti === 0 && i < 5 && g < oggiIso;
          const classi = [
            "striscia-giorno",
            g === scelto ? "scelto" : "",
            vuoto ? "vuoto" : "",
            g === oggiIso ? "oggi" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={g}
              type="button"
              className={classi}
              aria-pressed={g === scelto}
              disabled={futuro}
              onClick={() => scegli(g)}
            >
              <span className="striscia-nome">
                {NOMI_GIORNI[(daIso(g).getUTCDay() + 6) % 7]?.slice(0, 3)} {daIso(g).getUTCDate()}
              </span>
              <span className="striscia-ore">
                {minuti > 0 ? oreEMinuti(minuti) : vuoto ? "vuoto" : g === oggiIso ? "oggi" : ""}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
